import { groupBy } from "lodash";
import { useCallback } from "react";
import { Button, Form, Table } from "react-bootstrap";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";
import type { AccountHoldingResponse } from "../../../api/src/accounts/accounts.service";
import type {
  TradeRequest,
  RebalanceAccount,
} from "../../../api/src/rebalances/rebalances.service";
import FormFieldError from "../components/FormFieldError";
import SymbolField from "../components/SymbolField";
import { formatCurrency, formatPercent } from "../lib/numbers";
import { CASH_SYMBOLS } from "../lib/security";
import { getTradeSideFactor } from "./lib";

export type RebalanceEditForm = {
  accounts: {
    id: number;
    trades: TradeRequest[];
  }[];
};

const emptyTrade: TradeRequest = {
  rebalanceId: 0,
  accountId: 0,
  symbol: "",
  price: 0,
  amount: 0,
  side: "buy",
  isMarketTrade: true,
};

const TradesEditor = ({
  accountIndex,
  holdings,
  rebalanceAccount,
  disabled = false,
}: {
  accountIndex: number;
  holdings: AccountHoldingResponse[];
  rebalanceAccount: RebalanceAccount;
  disabled?: boolean;
}) => {
  const holdingsBySymbol = groupBy(holdings, "security.identifier");

  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
    getValues,
  } = useFormContext<RebalanceEditForm>();

  const {
    fields: tradeFields,
    append: appendTradeField,
    remove: removeTradeField,
  } = useFieldArray({
    name: `accounts.${accountIndex}.trades`,
    control,
  });

  // watch the entire trades array so we can inspect each row’s side
  const trades = watch(`accounts.${accountIndex}.trades`) || [];

  const { rebalanceId } = useParams<{ rebalanceId: string }>();

  const addTrade = useCallback(() => {
    const accountId = getValues(`accounts.${accountIndex}.id`);
    appendTradeField({
      ...emptyTrade,
      rebalanceId: Number(rebalanceId) || 0,
      accountId,
    });
  }, [appendTradeField, getValues, accountIndex, rebalanceId]);

  const addTradeForHolding = useCallback(
    (symbol: string, price: number = 0) => {
      const accountId = getValues(`accounts.${accountIndex}.id`);
      appendTradeField({
        ...emptyTrade,
        symbol,
        price,
        rebalanceId: Number(rebalanceId) || 0,
        accountId,
      });
    },
    [appendTradeField, getValues, accountIndex, rebalanceId],
  );

  // Get holdings that don't have associated trades (excluding cash and zero holdings)
  const holdingsWithoutTrades = holdings.filter(
    (holding) =>
      holding.security &&
      holding.units > 0 &&
      !CASH_SYMBOLS.includes(holding.security.identifier) &&
      !tradeFields.some(
        (trade) => trade.symbol === holding.security?.identifier,
      ),
  );

  const removeTrade = useCallback(
    (index: number) => () => {
      removeTradeField(index);
    },
    [removeTradeField],
  );

  return (
    <Table
      className="form-table align-top"
      style={
        {
          "--bs-table-cell-padding-y": "0.5rem",
          "--bs-table-cell-padding-x": "0.5rem",
        } as React.CSSProperties
      }
    >
      <thead>
        <tr>
          <th style={{ minWidth: 250 }}>Symbol</th>
          <th className="text-end">Current Shares</th>
          <th style={{ minWidth: 120 }}>Side</th>
          <th style={{ minWidth: 140 }}>Price</th>
          <th style={{ minWidth: 100 }}>Shares</th>
          <th className="text-end">Post-Trade Shares</th>
          <th className="text-end">Trade Value</th>
          <th className="text-end">Post-Trade Value</th>
          <th className="text-end" style={{ minWidth: 140 }}>
            Realized Gain/Loss
          </th>
          <th className="text-end" style={{ minWidth: 140 }}>
            Unrealized Gain/Loss
          </th>
          {disabled ? null : (
            <th style={{ width: 64 }} className="text-end"></th>
          )}
        </tr>
      </thead>
      <tbody>
        {tradeFields.map((trade, index) => {
          const side = trades[index]?.side;
          const amount = trades[index]?.amount ?? trade.amount;
          const price = trades[index]?.price ?? trade.price;
          const currentShares =
            holdingsBySymbol[trade.symbol]?.reduce(
              (sum, holding) => sum + holding.units,
              0,
            ) ?? 0;
          const postTradeShares =
            getTradeSideFactor(side ?? trade.side) *
              (side === "sell_all" ? currentShares : amount) +
            currentShares;

          const isTradeableFlag = trade.isMarketTrade;

          return (
            <tr key={trade.id}>
              <td>
                <SymbolField
                  fieldName={`accounts.${accountIndex}.trades.${index}.symbol`}
                  control={control}
                  register={register}
                  getValues={getValues}
                  disabled={disabled}
                  className={isTradeableFlag ? "" : "border-warning"}
                  includePrices={true}
                  onSelect={(suggestion) => {
                    if (
                      suggestion.currentPrice &&
                      suggestion.currentPrice > 0
                    ) {
                      setValue(
                        `accounts.${accountIndex}.trades.${index}.price`,
                        suggestion.currentPrice,
                        { shouldValidate: true },
                      );
                    }
                  }}
                />
                {!isTradeableFlag && (
                  <Form.Text className="text-danger d-block">
                    Not tradeable. Not included in trade execution.
                  </Form.Text>
                )}
                <FormFieldError
                  field={
                    errors.accounts?.[accountIndex]?.trades?.[index]?.symbol
                  }
                />
              </td>

              <td className="text-end">{currentShares.toLocaleString()}</td>

              <td>
                {(() => {
                  const reg = register(
                    `accounts.${accountIndex}.trades.${index}.side`,
                  );
                  return (
                    <Form.Select
                      {...reg}
                      disabled={disabled}
                      onChange={(e) => {
                        reg.onChange(e);
                        if (e.target.value === "sell_all") {
                          setValue(
                            `accounts.${accountIndex}.trades.${index}.amount`,
                            currentShares,
                            { shouldValidate: true },
                          );
                        }
                      }}
                    >
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                      <option value="sell_all">Sell All</option>
                    </Form.Select>
                  );
                })()}
              </td>

              <td>
                <div style={{ position: "relative" }}>
                  <span
                    style={{
                      position: "absolute",
                      left: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#6c757d",
                      pointerEvents: "none",
                      zIndex: 1,
                    }}
                  >
                    $
                  </span>
                  <Form.Control
                    style={{ paddingLeft: "20px", width: "100px" }}
                    {...register(
                      `accounts.${accountIndex}.trades.${index}.price`,
                    )}
                    disabled={disabled}
                  />
                </div>
              </td>

              <td>
                <Form.Control
                  type="number"
                  step="0.0001"
                  min="0"
                  className="text-end"
                  {...register(
                    `accounts.${accountIndex}.trades.${index}.amount`,
                    {
                      valueAsNumber: true,
                      validate: (value) => {
                        const strValue = value?.toString() || "";
                        return (
                          /^\d+(\.\d{0,4})?$/.test(strValue) ||
                          "Up to 4 decimals"
                        );
                      },
                    },
                  )}
                  readOnly={side === "sell_all"}
                  disabled={disabled}
                  onWheel={(e) => {
                    e.currentTarget.blur();
                  }}
                />
                <FormFieldError
                  field={
                    errors.accounts?.[accountIndex]?.trades?.[index]?.amount
                  }
                />
              </td>

              <td className="text-end">{postTradeShares.toLocaleString()}</td>

              <td className="text-end">
                {formatCurrency(
                  -getTradeSideFactor(side ?? trade.side) * amount * price,
                  2,
                )}
              </td>

              <td className="text-end">
                {formatCurrency(postTradeShares * price, 2)}
              </td>

              <td className="text-end">
                {(() => {
                  // Match by symbol since IDs are different between form and rebalance data
                  const tradesForSymbol = rebalanceAccount.trades.filter(
                    (t) => t.symbol === trade.symbol,
                  );
                  // Get the first trade for this symbol (most common case)
                  const originalTrade = tradesForSymbol[0];

                  // If no matching trade found, show placeholder
                  if (!originalTrade) return "—";

                  const totalRealizedGain =
                    (originalTrade.realizedGainLossSt ?? 0) +
                    (originalTrade.realizedGainLossLt ?? 0);

                  return totalRealizedGain !== 0
                    ? formatCurrency(totalRealizedGain, 2)
                    : "—";
                })()}
              </td>

              <td className="text-end">
                {(() => {
                  const holdingsForSymbol =
                    holdingsBySymbol[trade.symbol] || [];
                  if (holdingsForSymbol.length === 0) return "—";

                  const totalUnrealizedGain = holdingsForSymbol.reduce(
                    // `null` for a security with realized gains only
                    // (APP-1653): nothing to add, as when the API sent `0`.
                    (sum, h) => sum + (h.unrealizedGainLoss ?? 0),
                    0,
                  );
                  const totalValue = holdingsForSymbol.reduce(
                    (sum, h) => sum + h.value,
                    0,
                  );
                  const totalCostBasis = totalValue - totalUnrealizedGain;

                  if (totalCostBasis <= 0) return "—";

                  const percentage = totalUnrealizedGain / totalCostBasis;

                  return (
                    <div className="d-flex flex-column align-items-end">
                      <div>{formatCurrency(totalUnrealizedGain, 2)}</div>
                      <div
                        className="small text-muted"
                        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
                      >
                        {formatPercent(percentage, 2)}
                      </div>
                    </div>
                  );
                })()}
              </td>

              {!disabled && (
                <td className="text-end">
                  <Button
                    onClick={removeTrade(index)}
                    className="fw-bold"
                    style={{ width: "32px", height: "32px", padding: "0" }}
                  >
                    <img
                      src="/icons/trash.svg"
                      alt="Delete"
                      width="14"
                      height="14"
                      style={{ filter: "invert(1)" }}
                    />
                  </Button>
                </td>
              )}
            </tr>
          );
        })}
        {holdingsWithoutTrades.map((holding) => {
          const currentShares = holding.units;
          const price = holding.appraisedUnitPrice || 0;

          return (
            <tr key={`holding-${holding.securityId}`}>
              <td>
                <div className="fw-normal text-muted">
                  {holding.security?.identifier}
                </div>
                <Form.Text className="text-muted d-block">
                  {holding.security?.description ||
                    "Current holding (no trade planned)"}
                </Form.Text>
              </td>

              <td className="text-end">{currentShares.toLocaleString()}</td>

              <td className="text-muted">—</td>

              <td className="text-muted">
                {price > 0 ? formatCurrency(price, 2) : "—"}
              </td>

              <td className="text-muted">—</td>

              <td className="text-end">{currentShares.toLocaleString()}</td>

              <td className="text-end">—</td>

              <td className="text-end">
                {formatCurrency(currentShares * price, 2)}
              </td>

              <td className="text-end">
                {(() => {
                  const totalRealizedGain = holding.realizedGainLoss ?? 0;
                  if (totalRealizedGain === 0) return "—";

                  return formatCurrency(totalRealizedGain, 2);
                })()}
              </td>

              <td className="text-end">
                {(() => {
                  if (holding.value <= 0) return "—";

                  // `null` for a security with realized gains only
                  // (APP-1653); its `value` is `0`, so it shows "—" above.
                  const unrealizedGainLoss = holding.unrealizedGainLoss ?? 0;
                  const costBasis = holding.value - unrealizedGainLoss;
                  if (costBasis <= 0) return "—";

                  const percentage = unrealizedGainLoss / costBasis;

                  return (
                    <div className="d-flex flex-column align-items-end">
                      <div>{formatCurrency(unrealizedGainLoss, 2)}</div>
                      <div
                        className="small text-muted"
                        style={{ fontSize: "0.8rem", fontStyle: "italic" }}
                      >
                        {formatPercent(percentage, 2)}
                      </div>
                    </div>
                  );
                })()}
              </td>

              {!disabled && (
                <td className="text-end">
                  <Button
                    onClick={() =>
                      addTradeForHolding(
                        holding.security?.identifier || "",
                        price,
                      )
                    }
                    className="fw-bold"
                    style={{ width: "32px", height: "32px", padding: "0" }}
                  >
                    <img
                      src="/icons/edit.svg"
                      alt="Add Trade"
                      width="14"
                      height="14"
                      style={{ filter: "invert(1)" }}
                    />
                  </Button>
                </td>
              )}
            </tr>
          );
        })}
        {!disabled && (
          <tr>
            <td colSpan={9}></td>
            <td className="text-end">
              <Button
                onClick={addTrade}
                className="fw-bold"
                style={{ width: "32px", height: "32px", padding: "0" }}
              >
                +
              </Button>
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  );
};

export default TradesEditor;
